import { Search, SearchIcon } from 'lucide-react'
import { motion } from 'framer-motion'
import { useTheme } from 'next-themes'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import AuthButtons from '~/src/components/AuthButtons'
import RewardsHeaderTrigger from '~/src/components/RewardsHeaderTrigger'
import DupeLogo from '~/src/components/svg/DupeLogo'
import { cn } from '~/src/lib/utils'

const DupeInput = dynamic(() => import('~/src/components/DupeInput'), {
  ssr: false,
})

const HeaderSheetMenu = dynamic(
  () => import('~/src/components/HeaderSheetMenu'),
  {
    ssr: false,
  }
)

const UserInitials = dynamic(() => import('~/src/components/UserInitials'), {
  ssr: false,
})

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  expand?: boolean
  hideLogo?: boolean
  hideTryIt?: boolean
  hidePoints?: boolean
  sticky?: boolean
  iframe?: boolean
  clearBg?: boolean
  theme?: 'light' | 'dark'
  LogoComponent?: React.ReactNode
  inputClassName?: string
}

const LayoutHeader: React.FC<Props> = ({
  expand,
  hideLogo,
  hideTryIt,
  hidePoints,
  sticky,
  iframe,
  clearBg,
  theme,
  LogoComponent,
  className,
  inputClassName,
}) => {
  const { setTheme } = useTheme()
  const router = useRouter()
  const [shouldExpand, setShouldExpand] = useState(expand)
  const [showMobileSearch, setShowMobileSearch] = useState(false)
  const [isSheetOpen, setIsSheetOpen] = useState(false)

  // Check if on a chat page
  const isChatPage = (router?.pathname || '').startsWith('/chats/')

  const handleOnClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (iframe) {
      e.currentTarget.setAttribute('target', '_blank')
    }
  }

  useEffect(() => {
    if (shouldExpand) return

    if (expand) {
      setShouldExpand(true)
    }
  }, [expand, shouldExpand])

  useEffect(() => {
    if (theme) {
      setTheme(theme)
    }
  }, [setTheme, theme])

  return (
    <header
      className={cn(
        'dark:bg-darkest relative flex min-h-[60px] flex-col items-center gap-2 bg-white p-3 md:p-6',
        className,
        {
          'sticky top-0 z-10': sticky,
          'border-b-2 border-b-black': !hideTryIt,
          'border-b border-b-black/5': hideTryIt,
          'md:border-b-transparent md:bg-transparent md:dark:bg-transparent':
            clearBg,
        }
      )}
    >
      <div
        className={cn(
          'flex w-full items-center justify-between gap-2 md:min-h-[44px] md:gap-4'
        )}
      >
        <div className='flex items-center gap-2'>
          <HeaderSheetMenu
            setIsSheetOpen={setIsSheetOpen}
            isSheetOpen={isSheetOpen}
          />
          {!hideLogo && (
            <div className='relative flex items-center justify-start gap-2 md:min-w-[225px]'>
              <Link href='/' onClick={handleOnClick}>
                <DupeLogo className='h-8 w-auto md:h-auto' />
              </Link>
              {LogoComponent}
            </div>
          )}
        </div>
        <div className='flex items-center justify-end md:order-3 md:min-w-[225px]'>
          <div className='flex items-center gap-2'>
            {!hideTryIt && (
              <button
                className='flex items-center justify-center rounded-md p-1 md:hidden'
                aria-label='Toggle search'
                onClick={() => setShowMobileSearch(!showMobileSearch)}
              >
                <Search className='h-6 w-6' />
              </button>
            )}
            {!hidePoints && <RewardsHeaderTrigger />}
            {isChatPage && (
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className={cn(
                  'z-50 flex items-center gap-2',
                  'h-10 rounded-full border border-black/30 px-4 py-1 text-sm font-semibold backdrop-blur-md transition-colors hover:bg-black hover:text-white'
                )}
                onClick={() => router.push('/')}
              >
                <SearchIcon className='h-4 w-4' />
                New Search
              </motion.button>
            )}
            <AuthButtons />
            <UserInitials />
          </div>
        </div>
      </div>
      {!hideTryIt && (
        <div
          className={cn(
            'pointer-events-none flex w-full items-center justify-center md:absolute',
            {
              'hidden md:flex': !showMobileSearch,
            }
          )}
        >
          <div className='pointer-events-auto ml-auto w-full max-w-screen-sm md:mx-auto md:min-h-[56px]'>
            <DupeInput
              source='header'
              allowCamera
              ignoreBroadcast
              inputClassName={inputClassName}
              bgTransparent
            />
          </div>
        </div>
      )}
    </header>
  )
}

export default LayoutHeader
